import { DeleteOutlined, Notifications as NotificationsIcon, Search } from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    Skeleton,
    Stack,
    TextField,
    ThemeProvider,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import PageHeader from 'components/v2/common/pageHeader';
import PageBackground from 'components/v2/PageBackground';
import Result from 'components/v2/result';
import NotificationCard from '../components/notificationCard';

import {
    useDeleteNotification,
    useNotificationCategories,
    useNotifications,
    type NotificationCategoryFilter,
} from 'hooks/useNotifications';
import useTranslate from 'hooks/useTranslate';
import { RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import type { NotificationInterface } from 'types';
import { toTitleCase } from 'utils/v2/text';

const chipSx = (active: boolean) => ({
    fontWeight: active ? 600 : 500,
    fontSize: { xs: '0.7rem', sm: '0.75rem' },
    flexShrink: 0,
    height: { xs: 28, sm: 32 },
    maxWidth: { xs: 140, sm: 220 },
    borderRadius: 1.5,
    '& .MuiChip-label': {
        px: { xs: 1, sm: 1.25 },
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
});

const Notifications = () => {
    const navigate = useNavigate();
    const { translate } = useTranslate();
    const user = useSelector((state: RootState) => state.user.entity);
    const userId = user?.id || 0;

    const [category, setCategory] = useState<NotificationCategoryFilter>('all');
    const [searchInput, setSearchInput] = useState('');
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [confirmClearAll, setConfirmClearAll] = useState(false);
    const [isClearingAll, setIsClearingAll] = useState(false);

    const { data: categories = [], isLoading: isLoadingCategories } = useNotificationCategories();

    const {
        data: notifications = [],
        isLoading,
        isFetching,
        isError,
    } = useNotifications(userId, { category });

    const deleteNotification = useDeleteNotification();

    const filteredNotifications = useMemo(() => {
        const query = searchInput.trim().toLowerCase();
        if (!query) return notifications;
        return notifications.filter((n) => {
            const fullName = `${n.firstname ?? ''} ${n.lastname ?? ''}`.toLowerCase();
            const message = (n.message ?? '').toLowerCase();
            const program = (n.program_name ?? '').toLowerCase();
            return fullName.includes(query) || message.includes(query) || program.includes(query);
        });
    }, [notifications, searchInput]);

    const handleNotificationClick = (notification: NotificationInterface) => {
        if (notification.url) {
            navigate(notification.url);
        }
    };

    const handleDelete = async (notification: NotificationInterface) => {
        setDeletingId(notification.id);
        try {
            await deleteNotification.mutateAsync(notification.id);
        } catch {
            // Mutation invalidates on success; keep UI stable on failure
        } finally {
            setDeletingId(null);
        }
    };

    const handleClearAll = async () => {
        if (!notifications.length) return;
        setIsClearingAll(true);
        try {
            await Promise.all(notifications.map((n) => deleteNotification.mutateAsync(n.id)));
            setConfirmClearAll(false);
        } catch {
            // Keep dialog open so the user can retry
        } finally {
            setIsClearingAll(false);
        }
    };

    const showInitialLoading = isLoading && notifications.length === 0;
    const categoryLabel = category === 'all' ? translate('all') : toTitleCase(category);
    const showCount = !showInitialLoading && !isError;
    const hasList = filteredNotifications.length > 0;

    return (
        <ThemeProvider theme={appThemeV2}>
            <PageBackground
                maxWidth="lg"
                component="div"
                rootSx={{ minHeight: 'auto', overflow: 'visible' }}
                sx={{
                    px: { xs: 0, sm: 1 },
                    py: { xs: 1, sm: 1.5 },
                    maxWidth: '100% !important',
                }}
            >
                <Stack
                    sx={{
                        width: '100%',
                        bgcolor: 'background.paper',
                        borderRadius: { xs: 0, sm: 2 },
                        border: { xs: 'none', sm: '1px solid' },
                        borderColor: { sm: 'divider' },
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            flexShrink: 0,
                            width: '100%',
                            px: { xs: 1.5, sm: 2.5 },
                            pt: { xs: 2, sm: 2.5 },
                            pb: { xs: 1.25, sm: 1.5 },
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <PageHeader
                            title="notifications"
                            description="notificationsInternalDescription"
                            sx={{ mb: { xs: 1.5, sm: 2 } }}
                        >
                            {notifications.length > 0 && (
                                <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    startIcon={<DeleteOutlined />}
                                    onClick={() => setConfirmClearAll(true)}
                                    disabled={isClearingAll || deleteNotification.isPending}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderRadius: 1.5,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {translate('clearAll')}
                                </Button>
                            )}
                        </PageHeader>

                        <TextField
                            size="small"
                            fullWidth
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder={translate('searchNotificationsPlaceholder')}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={{
                                mb: 1.5,
                                maxWidth: { sm: 360 },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    bgcolor: 'background.default',
                                },
                            }}
                        />

                        <Stack
                            direction="row"
                            spacing={0.75}
                            sx={{ alignItems: 'center', minWidth: 0 }}
                        >
                            <Stack
                                direction="row"
                                spacing={0.75}
                                sx={{
                                    alignItems: 'center',
                                    overflowX: 'auto',
                                    flex: 1,
                                    minWidth: 0,
                                    py: 0.25,
                                    scrollbarWidth: 'none',
                                    '&::-webkit-scrollbar': { display: 'none' },
                                    WebkitOverflowScrolling: 'touch',
                                }}
                            >
                                <Chip
                                    label={translate('all')}
                                    size="small"
                                    clickable
                                    variant={category === 'all' ? 'filled' : 'outlined'}
                                    color={category === 'all' ? 'primary' : 'default'}
                                    onClick={() => setCategory('all')}
                                    sx={chipSx(category === 'all')}
                                />

                                {isLoadingCategories
                                    ? Array.from({ length: 3 }).map((_, i) => (
                                          <Skeleton
                                              key={i}
                                              variant="rounded"
                                              width={72}
                                              height={28}
                                              sx={{ flexShrink: 0, borderRadius: 1.5 }}
                                          />
                                      ))
                                    : categories.map((cat) => {
                                          const active = category === cat;
                                          return (
                                              <Chip
                                                  key={cat}
                                                  label={toTitleCase(cat)}
                                                  size="small"
                                                  clickable
                                                  variant={active ? 'filled' : 'outlined'}
                                                  color={active ? 'primary' : 'default'}
                                                  onClick={() => setCategory(cat)}
                                                  sx={chipSx(active)}
                                                  title={toTitleCase(cat)}
                                              />
                                          );
                                      })}
                            </Stack>

                            {isFetching && !isLoading && (
                                <CircularProgress
                                    size={14}
                                    sx={{ flexShrink: 0, color: 'text.disabled' }}
                                />
                            )}
                        </Stack>

                        {showCount && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: 'block',
                                    mt: 1,
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                }}
                            >
                                {filteredNotifications.length === 0
                                    ? translate('noResults')
                                    : `${filteredNotifications.length} ${translate(
                                          'notifications'
                                      ).toLowerCase()}`}
                                {category !== 'all' && (
                                    <>
                                        {' · '}
                                        <Box component="span" sx={{ fontWeight: 600 }}>
                                            {categoryLabel}
                                        </Box>
                                    </>
                                )}
                            </Typography>
                        )}
                    </Box>

                    <Box
                        sx={{
                            width: '100%',
                            opacity: isFetching && !isLoading ? 0.7 : 1,
                            transition: 'opacity 0.15s ease',
                        }}
                    >
                        {showInitialLoading ? (
                            <Stack sx={{ px: { xs: 1.5, sm: 2.5 }, py: { xs: 1.25, sm: 2 } }}>
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <Stack
                                        key={i}
                                        direction="row"
                                        spacing={1.5}
                                        sx={{
                                            py: 1.5,
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Skeleton variant="circular" width={36} height={36} />
                                        <Stack sx={{ flex: 1, minWidth: 0 }}>
                                            <Skeleton width="40%" height={14} />
                                            <Skeleton width="85%" height={18} sx={{ mt: 0.75 }} />
                                            <Skeleton width="50%" height={14} sx={{ mt: 0.75 }} />
                                        </Stack>
                                    </Stack>
                                ))}
                            </Stack>
                        ) : isError ? (
                            <Result
                                icon={
                                    <NotificationsIcon sx={{ fontSize: 48, color: 'error.main' }} />
                                }
                                title={translate('notificationsLoadErrorTitle')}
                                description={translate('notificationsLoadErrorDescription')}
                            />
                        ) : !hasList ? (
                            <Result
                                icon={
                                    <NotificationsIcon
                                        sx={{ fontSize: 48, color: 'text.disabled' }}
                                    />
                                }
                                title={
                                    searchInput.trim()
                                        ? translate('noMatchingNotifications')
                                        : category === 'all'
                                          ? translate('noNotifications')
                                          : translate('noNotificationsInCategory')
                                }
                                description={
                                    searchInput.trim()
                                        ? translate('noMatchingNotificationsHint')
                                        : category === 'all'
                                          ? translate('noNotificationsHint')
                                          : translate('noNotificationsInCategoryHint')
                                }
                            >
                                {(category !== 'all' || searchInput.trim()) && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => {
                                            setCategory('all');
                                            setSearchInput('');
                                        }}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        {translate('showAll')}
                                    </Button>
                                )}
                            </Result>
                        ) : (
                            <Stack sx={{ width: '100%' }}>
                                {filteredNotifications.map((notification, index) => (
                                    <NotificationCard
                                        key={notification.id}
                                        notification={notification}
                                        onNotificationClick={handleNotificationClick}
                                        onDelete={handleDelete}
                                        isDeleting={deletingId === notification.id}
                                        showDivider={index < filteredNotifications.length - 1}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Stack>

                {confirmClearAll && (
                    <ConfirmationDialog
                        title={translate('clearAllNotifications')}
                        content={translate('clearAllNotificationsConfirm')}
                        loading={isClearingAll}
                        onClose={() => {
                            if (!isClearingAll) setConfirmClearAll(false);
                        }}
                        onConfirm={handleClearAll}
                    />
                )}
            </PageBackground>
        </ThemeProvider>
    );
};

export default Notifications;
